import type { ReactNode } from 'react'
import { ChevronLeftIcon } from './icons'

export interface PageProps {
  /** The screen's h1. */
  title: ReactNode
  /** One line under the title: "5 days · Spain · buy in town". */
  subtitle?: ReactNode
  /** A hash to go back to ("#/trips"), shown as a back arrow before the title. */
  back?: string
  backLabel?: string
  /** Buttons at the right of the header. */
  actions?: ReactNode
  /**
   * Two columns at ≥ 960px: `children` on the left, `aside` on the right (sticky). On phones
   * the aside follows the main content.
   */
  aside?: ReactNode
  /** Content pinned to the bottom of the viewport (shop countdown, "Take to the shop"). */
  footer?: ReactNode
  className?: string
  children: ReactNode
}

/**
 * The frame every screen uses: a header with title/back/actions, a content column (max
 * ~720px, or two columns with `aside` on wide screens), and an optional fixed footer that sits
 * above the bottom tab bar.
 */
export function Page({
  title,
  subtitle,
  back,
  backLabel = 'Back',
  actions,
  aside,
  footer,
  className,
  children,
}: PageProps) {
  return (
    <div
      className={`rs-page ${aside ? 'rs-page--split' : ''} ${footer ? 'rs-page--footed' : ''} ${className ?? ''}`}
    >
      <header className="rs-page__head">
        {back ? (
          <a
            className="rs-iconbtn rs-page__back"
            href={back}
            aria-label={backLabel}
            title={backLabel}
          >
            <ChevronLeftIcon />
          </a>
        ) : null}
        <div className="rs-page__titles">
          <h1 className="rs-page__title">{title}</h1>
          {subtitle ? <p className="rs-page__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="rs-page__actions">{actions}</div> : null}
      </header>
      <div className="rs-page__body">
        <div className="rs-page__main">{children}</div>
        {aside ? <aside className="rs-page__aside">{aside}</aside> : null}
      </div>
      {footer ? <div className="rs-page__footer">{footer}</div> : null}
    </div>
  )
}
